import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { UserRound, GraduationCap, Search, Eye, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ViewAs = () => {
    const navigate = useNavigate();
    const [role, setRole] = useState('parent');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(null);

    const search = useCallback(async () => {
        setLoading(true);
        try {
            const params = { role };
            if (query.trim()) params.query = query.trim();
            const res = await axios.get(`${API_URL}/api/auth/impersonate/search`, { params, withCredentials: true });
            setResults(res.data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Search failed');
        } finally { setLoading(false); }
    }, [role, query]);

    useEffect(() => { search(); }, [role, search]);

    const startView = async (user) => {
        setStarting(user.id);
        try {
            await axios.post(`${API_URL}/api/auth/impersonate`, { target_user_id: user.id }, { withCredentials: true });
            toast.success(`Viewing as ${user.first_name} ${user.last_name}`);
            // Full reload so all components refetch with new session
            const dest = user.role === 'teacher' ? '/teacher/dashboard'
                : user.role === 'manager' ? '/manager/dashboard'
                : '/parent/dashboard';
            window.location.href = dest;
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to start impersonation');
            setStarting(null);
        }
    };

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="view-as-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2">
                        <Eye className="h-6 w-6 text-[#064E3B]" /> View As
                    </h1>
                    <p className="text-sm text-[#4B5563]">Explore the application exactly as any guardian, teacher, or manager sees it. Your super-admin session is safely stashed and can be restored with the &quot;Exit&quot; button that will appear on every page.</p>
                </div>

                <Card className="card-surface">
                    <CardHeader><CardTitle className="text-base font-['Outfit']">Choose a user</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                        <Tabs value={role} onValueChange={setRole}>
                            <TabsList>
                                <TabsTrigger value="parent" data-testid="view-as-tab-parent"><UserRound className="h-4 w-4 mr-1" />Guardians</TabsTrigger>
                                <TabsTrigger value="teacher" data-testid="view-as-tab-teacher"><GraduationCap className="h-4 w-4 mr-1" />Teachers</TabsTrigger>
                                <TabsTrigger value="manager" data-testid="view-as-tab-manager">Managers</TabsTrigger>
                            </TabsList>
                        </Tabs>

                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
                                <Input
                                    className="pl-9"
                                    placeholder={role === 'parent' ? 'Search by name, reg code, email…' : 'Search by name or email…'}
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
                                    data-testid="view-as-search"
                                />
                            </div>
                            <Button onClick={search} className="bg-[#064E3B] hover:bg-[#047857] text-white" data-testid="view-as-search-btn">
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                            </Button>
                        </div>

                        <div className="space-y-2">
                            {loading && <p className="text-sm text-[#6B7280]">Loading…</p>}
                            {!loading && results.length === 0 && <p className="text-sm text-[#6B7280]">No users found.</p>}
                            {results.map(u => (
                                <div key={u.id} className="flex items-center justify-between border border-[#E5E7EB] rounded-lg p-3 hover:bg-[#FAF9F6]" data-testid={`view-as-row-${u.id}`}>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-medium text-[#1F2937]">{u.first_name} {u.last_name}</p>
                                            {u.reg_code && <Badge variant="outline" className="font-mono text-xs">{u.reg_code}</Badge>}
                                            <Badge className="bg-[#F3F4F6] text-[#374151] hover:bg-[#F3F4F6] capitalize">{(u.role || '').replace('_', ' ')}</Badge>
                                            {(u.assigned_branches || []).length > 0 && (
                                                <span className="text-xs text-[#0D9488] font-medium">{u.assigned_branches.join(', ')}</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-[#4B5563] truncate">{u.email || '—'}</p>
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={() => startView(u)}
                                        disabled={starting === u.id}
                                        className="border-[#064E3B]/40 text-[#064E3B] hover:bg-[#064E3B]/5"
                                        data-testid={`view-as-btn-${u.id}`}
                                    >
                                        {starting === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}View
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
};

export default ViewAs;
